import { Combobox } from "@headlessui/react";
import { IconButton } from "@scandotcom/react";
import { IconMapPin } from "@tabler/icons-react";
import classNames from "classnames";
import React from "react";
import { useLocation } from "../../shared/useLocation";
import {
  getItemStyles,
  inputStyles,
  labelStyles,
  optionsStyles,
} from "./styles";

interface LocationInputProps {
  name?: string;
  defaultValue?: string;
  errorId?: string;
  className?: string;
  inputClassName?: string;
  onChange?: (value: string) => void;
}

export const LocationInput = React.forwardRef<
  HTMLInputElement,
  LocationInputProps
>((props, ref) => {
  const {
    getLocation,
    searchPlaces,
    suggestions,
    scriptLoaded,
    location,
    setLocation,
  } = useLocation(props.defaultValue ?? "");

  function updateLocation(value: string) {
    setLocation(value);
    props.onChange && props.onChange(value);
  }

  React.useEffect(() => {
    if (props.onChange) {
      props.onChange(location);
    }
  }, [location]);

  return (
    <Combobox
      name={props.name ?? "location"}
      data-testid="location-input"
      as="div"
      className={classNames("relative", props.className)}
      aria-invalid={!!props.errorId}
      aria-errormessage={props.errorId}
      value={location}
      onChange={updateLocation}
    >
      <div className={classNames(inputStyles, props.inputClassName)}>
        <div className="flex-grow">
          <Combobox.Label className={labelStyles}>Location</Combobox.Label>
          <Combobox.Input
            ref={ref}
            placeholder="Postcode or town"
            autoComplete="off"
            className="w-full placeholder:font-medium focus:outline-none"
            onChange={(event) => {
              updateLocation(event.target.value);
              searchPlaces(event.target.value);
            }}
            displayValue={(_) => location}
          />
        </div>
        <IconButton
          icon={IconMapPin}
          label="get location"
          className="ring-primary-300 h-7 w-7 shrink-0 rounded-lg text-neutral-700 transition hover:bg-neutral-200 focus:outline-none focus-visible:ring-2"
          onClick={getLocation}
        />
      </div>
      {Boolean(suggestions.length) && scriptLoaded && (
        <Combobox.Options
          className={classNames(optionsStyles, "lg:w-[calc(100%+104px+10px)]")}
        >
          <Combobox.Option value={location} />

          {suggestions?.map((loc) => {
            const name = loc.description.replace(", UK", "");
            return (
              <Combobox.Option
                key={loc.place_id}
                value={name}
                className={getItemStyles}
              >
                {name}
              </Combobox.Option>
            );
          })}
        </Combobox.Options>
      )}
    </Combobox>
  );
});
